import {useSelector} from 'react-redux'
import {useAccommodationTopOffers} from 'components/Accommodation/Offers/useAccommodationTopOffers'
import {useVioTag} from 'hooks/useVioTag'
import {getAccommodationPageHotelId} from 'modules/accommodation/selectors'
import {getCurrencyCode} from 'modules/meta/selectors'
import {
  getAnchorPriceNightlyPerRoom,
  getNumberOfRooms,
  getShowTotalPrices
} from 'modules/sapiSearch/selectors'
import {getAnchorPriceTotal} from 'modules/sapiSearch/utils'

import {Offer} from '@vio/core/src/offer/types/offer'
import {
  getDisplayPrice,
  getTotalPrice
} from '@vio/core/src/price/business/price'
import {VioTagDetails} from '@vio/core/src/tag/types'

export type RoomOfferVioTagDetails = Omit<VioTagDetails, 'tagMessage'> & {
  currencyCode: string
}

export const useRoomOfferVioTagDetails = ({
  offer
}: {
  offer: Offer
}): RoomOfferVioTagDetails => {
  const currencyCode = useSelector(getCurrencyCode)
  const {prices, tags} = offer

  const hotelId = useSelector(getAccommodationPageHotelId)

  const {offerEntity} = useAccommodationTopOffers({
    hotelId
  })

  const anchorPriceNightly = useSelector(state =>
    getAnchorPriceNightlyPerRoom(state, offerEntity)
  )
  const anchorPriceTotal = getAnchorPriceTotal(offerEntity)
  const showTotalPrices = useSelector(getShowTotalPrices)
  const numberOfRooms = useSelector(state => getNumberOfRooms(state, hotelId))
  const displayPrice = getDisplayPrice(prices)
  const {nightlyDisplayTotal} = displayPrice.chargeable

  const offerTotalPrice = getTotalPrice(displayPrice)
  const shownOfferPrice = showTotalPrices
    ? offerTotalPrice
    : nightlyDisplayTotal * numberOfRooms
  const anchorPrice = showTotalPrices
    ? (anchorPriceTotal ?? 0) * numberOfRooms
    : anchorPriceNightly * numberOfRooms

  const vioTagDetails = useVioTag({
    price: shownOfferPrice,
    anchorPrice,
    currencyCode,
    offerTags: tags
  })

  const {savingType, savingPrice, savingPercentage, isVioTagVisible} =
    vioTagDetails

  return {
    savingType,
    savingPrice,
    savingPercentage,
    isVioTagVisible,
    currencyCode
  }
}
